import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";
import axios from "axios";
import { ArrowLeft, Eye, MousePointerClick, Users, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell
} from "recharts";

const COLORS = ["#7c3aed", "#a855f7", "#c084fc", "#d8b4fe", "#e9d5ff"];

const MicrositeAnalyticsPage = () => {
  const { micrositeId } = useParams();
  const navigate = useNavigate();
  const [microsite, setMicrosite] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => { fetchData(); }, [micrositeId]);

  const fetchData = async () => {
    try {
      const msRes = await axios.get(`${API}/microsites/${micrositeId}`, { withCredentials: true });
      setMicrosite(msRes.data);
      const analyticsRes = await axios.get(`${API}/m/${msRes.data.slug}/analytics`, { withCredentials: true });
      setAnalytics(analyticsRes.data);
    } catch {
      toast.error("Gagal memuat analytics microsite");
      navigate("/microsites");
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <Button variant="ghost" className="mb-4" onClick={() => navigate("/microsites")}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Microsites
          </Button>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: "Plus Jakarta Sans" }}>
            {microsite?.title}
          </h1>
          <p className="text-muted-foreground mt-1 font-mono text-sm">
            {window.location.origin}/m/{microsite?.slug}
          </p>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <Card className="dashboard-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-primary/10 text-primary">
                  <Eye className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{analytics?.total_views || 0}</p>
                  <p className="text-sm text-muted-foreground">Total Views</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="dashboard-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-green-500/10 text-green-500">
                  <MousePointerClick className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{analytics?.total_link_clicks || 0}</p>
                  <p className="text-sm text-muted-foreground">Link Clicks</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="dashboard-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-purple-500/10 text-purple-500">
                  <TrendingUp className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {analytics?.total_views > 0
                      ? `${((analytics.total_link_clicks / analytics.total_views) * 100).toFixed(1)}%`
                      : "0%"}
                  </p>
                  <p className="text-sm text-muted-foreground">CTR</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Clicks Over Time */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="text-lg">Clicks Over Time</CardTitle>
              <CardDescription>Klik link per hari</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                {analytics?.clicks_by_date?.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={analytics.clicks_by_date}>
                      <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }} />
                      <Line type="monotone" dataKey="clicks" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ fill: "hsl(var(--primary))" }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-muted-foreground">Belum ada data</div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Devices */}
          <Card className="dashboard-card">
            <CardHeader>
              <CardTitle className="text-lg">Devices</CardTitle>
              <CardDescription>Perangkat pengunjung</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                {analytics?.devices?.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={analytics.devices} cx="50%" cy="50%" innerRadius={50} outerRadius={80}
                        paddingAngle={5} dataKey="value" nameKey="name"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                        {analytics.devices.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-muted-foreground">Belum ada data</div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Clicks per Link */}
        <Card className="dashboard-card">
          <CardHeader>
            <CardTitle className="text-lg">Performa per Link</CardTitle>
            <CardDescription>Jumlah klik tiap link di microsite ini</CardDescription>
          </CardHeader>
          <CardContent>
            {analytics?.links?.length > 0 ? (
              <div className="space-y-3">
                {analytics.links
                  .sort((a, b) => b.clicks - a.clicks)
                  .map((link, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-bold text-sm flex-shrink-0">
                          {i + 1}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{link.title}</p>
                          <p className="text-xs text-muted-foreground truncate">{link.url}</p>
                        </div>
                      </div>
                      <span className="font-bold text-primary ml-4 flex-shrink-0">
                        {link.clicks} clicks
                      </span>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">Belum ada data klik</div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default MicrositeAnalyticsPage;